import { useEffect, useMemo, useState } from "react";
import { Input, SelectInput, Switch, SwitchSelect } from "@repo/ui";
import { validateAction } from "~/pages/administration/workflows-page/workflows-dialog/views/action-view/utils/validate-actions";
import type { CreateActionRequest, PrintPdfConfiguration } from "~/types/api";
import pdfPresets from "~/utils/pdf-presets.json";
import { WorkflowsInputControl } from "../workflows-input-control";
import { getPathFieldMeta, type PathField } from "./utils/path-field-meta";

export type InputMode = "create" | "edit";

const METHOD_OPTIONS = [
  { label: "Single Page", value: "Single" },
  { label: "Spread", value: "Spread" },
  { label: "All", value: "All" },
];

type Props = {
  action: CreateActionRequest | null;
  mode?: InputMode;
  pathFields?: PathField[];
  dynamicPresetFields?: PathField[];
  onSave: (updated: CreateActionRequest) => void;
  onHasUnsavedChanges?: (hasChanges: boolean, onDiscard: () => void) => void;
};

export const ActionInput = ({
  action,
  mode: _mode = "create",
  pathFields = [],
  dynamicPresetFields = [],
  onSave,
  onHasUnsavedChanges,
}: Props) => {
  const [localCopy, setLocalCopy] = useState<CreateActionRequest | null>(action);
  const [originalAction, setOriginalAction] = useState<CreateActionRequest | null>(action);

  const config = (localCopy?.configuration ?? null) as PrintPdfConfiguration | null;

  const [useDynamicPreset, setUseDynamicPreset] = useState<boolean>(
    () => !!(action?.configuration as PrintPdfConfiguration | null)?.presetNameFieldId
  );

  const { outputOptions } = useMemo(() => getPathFieldMeta(pathFields), [pathFields]);

  const presetOptions = useMemo(
    () => dynamicPresetFields.map((field) => ({ label: field.name, value: field.id })),
    [dynamicPresetFields]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!originalAction || !localCopy) return false;
    return JSON.stringify(originalAction) !== JSON.stringify(localCopy);
  }, [originalAction, localCopy]);

  const errors = useMemo(() => {
    if (!localCopy) return {};
    return validateAction(localCopy, useDynamicPreset);
  }, [localCopy, useDynamicPreset]);

  const hasErrors = Object.values(errors).some(Boolean);

  const handleDiscard = () => {
    setLocalCopy(originalAction);
    setUseDynamicPreset(
      !!(originalAction?.configuration as PrintPdfConfiguration | null)?.presetNameFieldId
    );
  };

  useEffect(() => {
    onHasUnsavedChanges?.(hasUnsavedChanges, handleDiscard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges]);

  const handleSaveInternal = () => {
    if (!localCopy || hasErrors) return;
    onSave(localCopy);
    setOriginalAction(localCopy);
  };

  const updateConfig = (patch: Partial<PrintPdfConfiguration>) => {
    if (!localCopy || !config) return;
    setLocalCopy({ ...localCopy, configuration: { ...config, ...patch } });
  };

  if (!action || !localCopy || !config) {
    return <div className="min-w-3xs p-4 text-center text-gray-400">Select an action to edit</div>;
  }

  return (
    <div className="relative flex min-w-60 flex-col gap-4 border-b border-b-gray-300 pb-4">
      <Input
        label="Action Name"
        value={localCopy.name}
        onChange={(e) => setLocalCopy({ ...localCopy, name: e.target.value })}
        error={errors.name}
        required={true}
      />
      <div className="flex items-center justify-between gap-2 pr-2">
        <label className="text-[15px] leading-none text-gray-900" htmlFor="dynamic-preset-switch">
          Dynamic Presets
        </label>
        <Switch
          id="dynamic-preset-switch"
          disabled={false}
          checked={useDynamicPreset}
          onCheckedChange={(val) => {
            setUseDynamicPreset(val);
            const originalConfig = action.configuration as PrintPdfConfiguration | undefined;
            if (val) {
              updateConfig({ presetName: originalConfig?.presetName ?? null });
            } else {
              updateConfig({ presetNameFieldId: originalConfig?.presetNameFieldId ?? null });
            }
          }}
          className="data-[state=checked]:bg-gxe-primary bg-gray-300"
        />
      </div>
      {useDynamicPreset ? (
        <SelectInput<number>
          label="Dynamic Preset"
          options={presetOptions}
          value={config.presetNameFieldId || undefined}
          onChange={(val) => {
            if (typeof val === "number") updateConfig({ presetNameFieldId: val });
          }}
          placeholder="Select dynamic print preset"
          error={errors.presetNameFieldId}
          required={true}
        />
      ) : (
        <SelectInput
          label="Print Preset"
          options={pdfPresets.map((preset) => ({ label: preset, value: preset }))}
          value={config.presetName ?? ""}
          onChange={(val) => {
            if (typeof val === "string") updateConfig({ presetName: val });
          }}
          placeholder="Select print preset"
          error={errors.presetName}
          required={true}
        />
      )}

      <SelectInput<number>
        label="Destination Path"
        options={outputOptions}
        value={config.outputFieldId || undefined}
        onChange={(val) => {
          if (typeof val === "number") updateConfig({ outputFieldId: val });
        }}
        placeholder="Select a path field"
        error={errors.outputFieldId}
        required={true}
      />

      <Input
        label="File Name"
        value={config.fileNameTemplate ?? ""}
        onChange={(e) => updateConfig({ fileNameTemplate: e.target.value })}
        error={errors.fileNameTemplate}
        required={true}
      />

      <SelectInput
        label="Page Mode"
        options={METHOD_OPTIONS}
        value={config.method ?? "Spread"}
        error={errors.method}
        onChange={(val) => {
          if (typeof val === "string")
            updateConfig({ method: val as PrintPdfConfiguration["method"] });
        }}
        required={true}
      />

      <SwitchSelect
        disabled={false}
        checked={config.promptUser ?? false}
        onCheckedChange={(val) => updateConfig({ promptUser: val })}
        labelClassName="mb-0 text-nowrap"
        containerClassName="flex flex-wrap items-center gap-3 max-md:justify-end max-md:gap-10"
        label="Ask user for pages"
      />

      {hasUnsavedChanges && (
        <div className="absolute right-0 bottom-3 ml-auto flex gap-2">
          <WorkflowsInputControl controlType="cancel" onClick={handleDiscard} />
          <WorkflowsInputControl
            controlType="confirm"
            onClick={handleSaveInternal}
            disabled={hasErrors}
          />
        </div>
      )}
    </div>
  );
};
